'use client';

import React, { useState, useEffect, useRef } from 'react';
import ColorPicker from 'react-best-gradient-color-picker';
import { Move, RotateCcw, Maximize2, Trash2, Upload, ImageIcon, Palette, Globe, Mountain, Box, Sparkles } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { cn } from '../lib/utils';
import __ from '../utils/translation';
import { getRegisteredAPI, deleteRegisteredAPI, registerAPI, getUserId } from "../API.js";
import { Switch } from "./ui/switch"
import $ from "jquery";

// ============================================================================
// CONSTANTS
// ============================================================================

type LightItem = {
  id: string;
  ref: any;
  color: string;
  intensity: number;
};

const GROUND_TEXTURES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const HDRI_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
  id: i + 1,
  thumb: `./background/hdris/thumbs/${i + 1}.webp`,
}));

const SCENE_PRESETS = [
  { id: 'scene1', pos: [0, -0.8, 3.4], rot: [0, 0, 0], scale: 0.95, name: 'Default' },
  { id: 'scene3', pos: [0, -1.2, -4.5], rot: [0, 0, 0], scale: 1.11, name: 'Far View' },
  { id: 'scene4', pos: [0, 0.22, 0], rot: [0, -1.434, 0], scale: 0.62, name: 'Side Angle' },
  { id: 'scene5', pos: [0.189, 0, 5.158], rot: [0, -1.53, 0], scale: 1.09, name: 'Close Up' },
  { id: 'scene7', pos: [0, -0.59, 3.67], rot: [0, 0, 0], scale: 0.46, name: 'Compact' },
  { id: 'scene8', pos: [0.35, -0.36, 4.11], rot: [0, -1.47, 0], scale: 0.478, name: 'Offset' },
  { id: 'scene9', pos: [0, -0.37, 0], rot: [0, 0, 0], scale: 1.287, name: 'Zoomed' },
  { id: 'scene10', pos: [0.044, 0, 3.67], rot: [0, 0, 0], scale: 1.15, name: 'Standard' },
  { id: 'scene11', pos: [-0.01, 0.01, 0.14], rot: [0, 0, 0], scale: 0.959, name: 'Centered' },
  { id: 'scene12', pos: [0, 0, -0.11], rot: [0, 0, 0], scale: 1.28, name: 'Wide' },
  { id: 'scene13', pos: [0, -2, -0.11], rot: [0, -90, 0], scale: 2.5, name: 'Panoramic' },
];

const STAGE_PRESETS = [
  { id: 'stage1', name: 'CC4 PRESSET 1 - BLUE FILL' },
  { id: 'stage2', name: 'CC4 PRESSET 1 - AQUA' },
  { id: 'stage3', name: 'CC4 PRESSET 1 - CINEMATIC DARK 1' },
  { id: 'stage4', name: 'CC4 PRESSET 1 - CINEMATIC DARK 2' },
  { id: 'stage5', name: 'CC4 PRESSET 1 - DOWN LUMEN' },
  { id: 'stage6', name: 'CC4 PRESSET 1 - LEADING ROLE' },
  { id: 'stage7', name: 'CC4 PRESSET 1 - PURPLE ACID' },
]

const BACKGROUND_TABS = [
  { id: 'color', icon: Palette, label: 'Color' },
  { id: 'texture', icon: ImageIcon, label: 'Image' },
  { id: 'hdri', icon: Globe, label: 'HDRI' },
  { id: 'ground', icon: Mountain, label: 'Scene' },
  { id: 'cena', icon: Box, label: '3D' },
  { id: 'stage', icon: Box, label: 'Palcos' },
];

// URL do backend (Express, onde ficam as rotas de geração por IA) — mesmo
// padrão já usado no simulador (index.tsx / logo-picker.tsx).
const BACKEND_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : 'https://simuladorksports.com/aisten-kings2-backend';

type BackgroundItem = {
  name: string;
  url: string;        // 👈 SEMPRE a URL final da imagem
  isTemp?: boolean;   // preview local
};

// ============================================================================
// COMPONENT
// ============================================================================

const BackgroundScene: React.FC = () => {
  const [color, setColor] = useState('rgba(255,255,255,1)');
  const [activeTab, setActiveTab] = useState('color');
  const [transformMode, setTransformMode] = useState('pos');
  const [opacity, setOpacity] = useState([100]);
  const [selectedGround, setSelectedGround] = useState<number | null>(null);
  const [selectedHdri, setSelectedHdri] = useState<number | null>(null);
  const [backgrounds, setBackgrounds] = useState<any[]>([]);
  const [fundoChecked, setFundoChecked] = useState(false);
  const [lights, setLights] = useState<LightItem[]>([]);
  const [aiBackgroundPrompt, setAiBackgroundPrompt] = useState('');
  const [isGeneratingBackground, setIsGeneratingBackground] = useState(false);
  const [aiBackgroundError, setAiBackgroundError] = useState<string | null>(null);
  const [generatedPreview, setGeneratedPreview] = useState<{ url: string; file: File } | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    // função acessível globalmente
    (window as any).setStateFundo = (value: any) => {
      setFundoChecked(value);
    };
  }, []);

  useEffect(() => {
    const populateBackgrounds = async () => {
      const userId = getUserId();
      if (!userId) return;

      const list = await getRegisteredAPI("backgrounds", userId);

      const safeList = list ?? [];

      const mapped = safeList.map((bg: { name: string }) => ({
        name: bg.name,
        url: `https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/backgrounds/${userId}/${bg.name}`,
        isTemp: false,
      }));

      setBackgrounds(mapped);
    };

    populateBackgrounds();
  }, []);

  (window as any).addBackground = (bg: any) => {
    setBackgrounds((prev) => [...prev, bg]);
  };


  // --------------------------------------------------------------------------
  // EVENT HANDLERS
  // --------------------------------------------------------------------------

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    if (typeof window !== 'undefined' && (window as any)['updateColor']) {
      (window as any)['updateColor'](newColor);
    }
  };

  const handleSceneTransform = (style: string) => {
    setTransformMode(style);
    if (typeof window !== 'undefined' && (window as any)['transformType']) {
      (window as any)['transformType'](style);
    }
  };

  const deleteScene = () => {
    if (typeof window !== 'undefined' && (window as any)['deleteFile']) {
      (window as any)['deleteFile']();
    }
  };

  const resetTransform = (type: string) => {
    if (typeof window !== 'undefined' && (window as any)['resetTransform']) {
      (window as any)['resetTransform'](type);
    }
  };

  const handleOpacityChange = (value: number[]) => {
    setOpacity(value);
    $("#background-panel").css("opacity", value.toString())
  };

  const handleGroundSelect = (num: number) => {
    setSelectedGround(num);
  };

  const handleHdriSelect = (hdriId: number) => {
    setSelectedHdri(hdriId);
    if (typeof window !== 'undefined' && (window as any)['selectHdri']) {
      (window as any)['selectHdri'](hdriId);
    }
  };

  const handleDeleteBackground = async (name: string) => {
    /*if ((window as any).deleteBackground) {

      if (window.confirm("Tem certeza que deseja apagar esse fundo?")) {
        deleteRegisteredAPI(getUserId() + '/' + name, "backgrounds");
      }

      (window as any).deleteBackground(name);
    }*/

    if (window.confirm("Tem certeza que deseja apagar esse fundo?")) {
      deleteRegisteredAPI(getUserId() + '/' + name, "backgrounds");

      setBackgrounds(prev => prev.filter(bg => bg.name !== name));
    }
  };

  const handleSelectBackground = (url: string) => {
    if ((window as any).selectBackground) {
      (window as any).selectBackground(url);
    }
  };

  const handleUploadBackground = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    setBackgrounds(prev => [
      {
        name: file.name,
        url: previewUrl,   // 👈 blob:
        isTemp: true,
      },
      ...prev,
    ]);

    e.target.value = "";
  };

  // Gera um fundo de cena via IA (prompt de texto → imagem) e já registra
  // ele na lista de fundos salvos, do mesmo jeito que um upload manual.
  const handleGenerateBackground = async () => {
    const prompt = aiBackgroundPrompt.trim();
    if (!prompt) return;

    setIsGeneratingBackground(true);
    setAiBackgroundError(null);
    setGeneratedPreview(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/generate-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || 'Não foi possível gerar o fundo.');
      }

      // Converte o dataURL (base64) devolvido pelo backend num File, pra
      // reaproveitar o mesmo registerAPI já usado pelo upload manual.
      const blob = await (await fetch(data.dataUrl)).blob();
      const ext = (data.meta?.mimeType || 'image/webp').split('/')[1] || 'webp';
      const file = new File([blob], `fundo-ia.${ext}`, { type: data.meta?.mimeType || 'image/webp' });

      if (!isMountedRef.current) return;

      // Mostra o resultado numa área isolada, SEM mexer na lista
      // compartilhada de fundos (backgrounds) — evita reconciliar a grade
      // inteira logo depois de gerar, que estava causando um crash do React
      // (aparentemente um conflito com a animação de troca de aba do Radix).
      // O usuário decide explicitamente se quer usar/salvar esse resultado.
      const previewUrl = URL.createObjectURL(blob);
      setGeneratedPreview({ url: previewUrl, file });
      setAiBackgroundPrompt('');
    } catch (err: any) {
      console.error('[BackgroundScene] erro ao gerar fundo por IA:', err);
      if (isMountedRef.current) {
        setAiBackgroundError(err?.message || 'Erro ao gerar o fundo. Tente novamente.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsGeneratingBackground(false);
      }
    }
  };

  // Chamado quando o usuário confirma que quer usar/salvar o fundo gerado —
  // só AGORA ele entra na lista compartilhada de fundos.
  const handleUseGeneratedPreview = () => {
    if (!generatedPreview) return;
    const { file, url } = generatedPreview;

    // Aplica no simulador e já entra na lista, usando a mesma URL local
    // (blob) da prévia — nada de esperar/rebuscar/mesclar depois, que era
    // o que estava causando o erro de reconciliação do React.
    handleSelectBackground(url);
    setBackgrounds(prev => [{ name: file.name, url, isTemp: true }, ...prev]);
    setGeneratedPreview(null);

    // Salva permanentemente em segundo plano — se der certo ou errado, não
    // mexe mais em nenhum estado do React depois disso. Ao recarregar a
    // página, a lista normal (useEffect no mount) já busca do servidor e
    // mostra com a URL definitiva.
    registerAPI({ name: 'fundo-ia', image: file }, 'backgrounds', true, null)
      .catch((e) => console.error('[BackgroundScene] erro ao registrar fundo gerado:', e));
  };

  const handleChangeFundo = (checked: boolean) => {
    if (checked)
      $("#background-panel").css("background-size", "cover")
    else
      $("#background-panel").css("background-size", "contain")
  }

  const handleStageImageUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    // aplicar textura no palco (Three.js)
  };

  const handleDeleteStage = () => {
    // remover palco da cena
    if (typeof window !== 'undefined' && (window as any)['deleteFile']) {
      (window as any)['deleteFile']();
    }
  };


  const handleDownloadStage = () => {
    // remover palco da cena
    const a = document.createElement("a");
    a.href = "./models/stages/STAGE.webp";
    a.download = "palco.png";
    a.click();
  };

  (window as any)["extractLightsFromScene"] = function (scene: any) {
    const sceneLights: LightItem[] = [];

    scene.traverse((obj: any) => {
      if (obj.isLight) {
        const light = obj;

        sceneLights.push({
          id: light.uuid,
          ref: light,
          color: `#${light.color.getHexString()}`,
          intensity: light.intensity,
        });
      }
    });

    setLights(sceneLights);
  };

  const updateLight = (
    id: string,
    changes: Partial<Pick<LightItem, 'color' | 'intensity'>>
  ) => {
    setLights((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;

        // Three.js update (imutável no React, mutável na cena)
        if (changes.color) l.ref.color.set(changes.color);
        if (changes.intensity !== undefined)
          l.ref.intensity = changes.intensity;

        return { ...l, ...changes };
      })
    );
  };

  const applyStagePreset = (id: any) => {
    // aplicar preset
  };


  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <Card className="w-full max-w-md border-border/50 bg-card/95 backdrop-blur-sm p-0">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold tracking-tight">
          {__('Background')}
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          {__('Customize your scene background')}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 p-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-20">
            {BACKGROUND_TABS.map(({ id, icon: Icon, label }) => (
              <TabsTrigger
                key={id}
                value={id}
                data-option={label}
                className="background-type flex items-center gap-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="size-3.5" />
                <span className="hidden sm:inline">{__(label)}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Color Tab */}
          <TabsContent value="color" className="mt-4 space-y-4">
            <div className="rounded-lg border border-border/50 p-4 bg-muted/30">
              <ColorPicker className="!w-full" value={color} onChange={handleColorChange} />
            </div>
          </TabsContent>

          {/* Image/Texture Tab */}
          <TabsContent value="texture" className="mt-4 space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{__('Background Opacity')}</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={opacity}
                    onValueChange={handleOpacityChange}
                    max={1}
                    step={0.05}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {opacity[0]}%
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-muted cloneOpt">
                <Label htmlFor="" className="cursor-pointer text-sm font-medium">
                  Cobrir Fundo
                </Label>
                <Switch
                  id="state-fundo"
                  checked={fundoChecked}
                  onCheckedChange={(checked) => {
                    setFundoChecked(checked);
                    handleChangeFundo(checked);
                  }}
                />
              </div>

              <Separator className="bg-border/50" />

              <div className="space-y-2">
                <Label className="text-sm font-medium">{__('Upload Image')}</Label>
                <label
                  htmlFor="uploadFile"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border/50 rounded-lg cursor-pointer bg-muted/20 hover:bg-muted/40 transition-colors"
                >
                  <Upload className="size-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground text-center px-4">
                    {__('Drag and drop images or click to browse')}
                  </span>
                  <input
                    type="file"
                    id="uploadFile"
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadBackground}
                  />
                </label>
              </div>

              <Separator className="bg-border/50" />

              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-primary" />
                  {__('Gerar fundo por IA')}
                </Label>
                <textarea
                  value={aiBackgroundPrompt}
                  onChange={(e) => setAiBackgroundPrompt(e.target.value)}
                  placeholder={__('Descreva o fundo que você quer, ex: "Loja de roupas moderna com iluminação suave"')}
                  rows={3}
                  disabled={isGeneratingBackground}
                  className="w-full text-sm bg-muted/30 border border-border/50 rounded-lg px-3 py-2 outline-none focus:border-primary/50 resize-none disabled:opacity-60"
                />
                {aiBackgroundError && (
                  <p className="text-xs text-destructive">{aiBackgroundError}</p>
                )}
                <Button
                  onClick={handleGenerateBackground}
                  disabled={isGeneratingBackground || !aiBackgroundPrompt.trim()}
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isGeneratingBackground ? (
                    <>
                      <div className="size-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                      {__('Gerando...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="size-4" />
                      {__('Gerar Fundo')}
                    </>
                  )}
                </Button>

                {generatedPreview && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 space-y-2">
                    <img
                      src={generatedPreview.url}
                      alt="Fundo gerado"
                      className="w-full h-32 object-cover rounded"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1" onClick={handleUseGeneratedPreview}>
                        {__('Usar este fundo')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setGeneratedPreview(null)}
                      >
                        {__('Descartar')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 flex flex-wrap">
                <span className='t-2 text-primary text-justify'>* Para cadastrar fundos permanentes, vá a tela de projetos e no menu lateral esquerdo cadastre o fundo desejado.</span>
                {/**<span className='t-2 text-primary text-justify'>* Para definir um fundo padrão, clique no botão verde "P" em cima da imagem desejada.</span> */}
                <span className='t-2 text-primary text-justify'>* Para deletar um fundo salvo, clique no botão vermelho "X" em cima da imagem desejada.</span>
              </div>

              <div className='w-full flex justify-center'>
                <Button id="remove-iframe-background">Remover fundo cadastrado iframe</Button>
              </div>


              <div id="registered-background" className="grid grid-cols-3 gap-2">
                {backgrounds.map((bg, index) => (
                  <div
                    key={bg.url ?? index}
                    className="registered-background relative"
                  >
                    {/* Delete */}
                    <button
                      className="delete-registered-background absolute top-1 right-1 z-10"
                      onClick={() => handleDeleteBackground(bg.name)}
                    >
                      X
                    </button>

                    {/* Iframe */}
                    <button
                      className="iframe-registered-background absolute top-1 left-1 z-10"
                      onClick={() => handleSelectBackground(bg.url)}
                    >
                      I
                    </button>

                    {/* Select */}
                    <button
                      className="main-registered-background absolute top-1 left-1 z-10"
                      onClick={() => handleSelectBackground(bg.url)}
                    >
                      P
                    </button>

                    {/* Image */}
                    <img
                      src={bg.url}
                      alt={bg.name}
                      className="thumb-background-texture backUser w-full h-full object-cover rounded"
                    />

                    {bg.isTemp && (
                      <span className="absolute bottom-1 right-1 text-[10px] bg-black/60 text-white px-1 rounded">
                        preview
                      </span>
                    )}
                  </div>
                ))}
              </div>


            </div>
          </TabsContent>

          {/* HDRI Tab */}
          <TabsContent value="hdri" className="mt-4 max-h-[calc(100vh-250px)] overflow-y-auto overflow-x-hidden">
            <div className="space-y-3">
              <Label className="text-sm font-medium">{__('Environment Maps')}</Label>

              {/* Botão para selecionar HDR local */}
              <label
                htmlFor="file-hdri"
                className="h-[100px] w-full flex items-center justify-center aspect-square rounded-lg border-2 border-dashed border-border cursor-pointer text-sm text-gray-500 hover:border-primary hover:text-primary transition-all"
              >
                {__('Upload HDR')}
              </label>
              <input
                type="file"
                id="file-hdri"
                accept=".hdr,.exr"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  // Cria um URL temporário para carregar o HDR
                  const url = URL.createObjectURL(file) as any;

                  // Chame sua função para aplicar o HDR na cena
                  handleHdriSelect(url);

                  (window as any)["loadHDR"](url);
                }}
              />

              <div id="thumb-background-hdri" className="grid grid-cols-3 gap-2">
                {HDRI_OPTIONS.map(({ id, thumb }) => (
                  <button
                    key={id}
                    data-hdri={id}
                    onClick={() => handleHdriSelect(id)}
                    className={cn(
                      "thumb-background-hdri relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105",
                      selectedHdri === id
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border/50 hover:border-border"
                    )}
                  >
                    <img
                      src={thumb || "/placeholder.svg"}
                      alt={`HDRI ${id}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Ground/Scene Tab */}
          <TabsContent value="ground" className="mt-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium">{__('Ground Textures')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {GROUND_TEXTURES.map((num) => (
                  <button
                    key={num}
                    data-ground={num}
                    onClick={() => handleGroundSelect(num)}
                    className={cn(
                      "back-ground relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 bg-center",
                      selectedGround === num
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border/50 hover:border-border"
                    )}
                    style={{ backgroundImage: `url(./grass/${num}.webp)`, backgroundSize: 'cover' }}
                  >
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors " />
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* 3D Scene Tab */}
          <TabsContent value="cena" className="mt-4 space-y-4">
            <div className="space-y-4">
              {/* Transform Controls */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{__('Scene Controls')}</Label>
                <input id="obj-scene-upload" type='file' accept='.glb'></input>
                <Select value={transformMode} onValueChange={handleSceneTransform}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={__('Select transform mode')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pos">
                      <div className="flex items-center gap-2">
                        <Move className="size-4" />
                        {__('Position')}
                      </div>
                    </SelectItem>
                    <SelectItem value="rot">
                      <div className="flex items-center gap-2">
                        <RotateCcw className="size-4" />
                        {__('Rotation')}
                      </div>
                    </SelectItem>
                    <SelectItem value="scale">
                      <div className="flex items-center gap-2">
                        <Maximize2 className="size-4" />
                        {__('Scale')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator className="bg-border/50" />

              {/* Reset Buttons */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{__('Reset Transform')}</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetTransform('pos')}
                    className="flex-1"
                  >
                    <Move className="size-3.5 mr-1.5" />
                    {__('Position')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetTransform('rot')}
                    className="flex-1"
                  >
                    <RotateCcw className="size-3.5 mr-1.5" />
                    {__('Rotation')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetTransform('esc')}
                    className="flex-1"
                  >
                    <Maximize2 className="size-3.5 mr-1.5" />
                    {__('Scale')}
                  </Button>
                </div>
              </div>

              <Separator className="bg-border/50" />

              {/* Delete Scene */}
              <Button
                variant="destructive"
                size="sm"
                onClick={deleteScene}
                className="w-full"
              >
                <Trash2 className="size-4 mr-2" />
                {__('Delete Scene')}
              </Button>

              <Separator className="bg-border/50" />

              {/* Scene Presets */}
              <div className="space-y-3 max-h-[calc(100vh-500px)] overflow-y-auto">
                <Label className="text-sm font-medium">{__('Scene Presets')}</Label>
                <div className="grid grid-cols-3 gap-2 ">
                  {SCENE_PRESETS.map(({ id, pos, rot, scale, name }) => (
                    <button
                      key={id}
                      id={id}
                      data-posx={pos[0]}
                      data-posy={pos[1]}
                      data-posz={pos[2]}
                      data-rotx={rot[0]}
                      data-roty={rot[1]}
                      data-rotz={rot[2]}
                      data-scale={scale}
                      className="scene-up flex items-center justify-center h-10 text-xs font-medium rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 hover:border-border transition-colors"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Stage Tab */}
          <TabsContent value="stage" className="mt-4 space-y-4">
            <div className="space-y-4">

              {/* Upload de imagem */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{__('Imagem do Palco')}</Label>
                <input
                  type="file"
                  accept="image/*"
                  id="upload-stage-image"
                  className="block w-full text-sm text-primary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-muted file:text-sm file:font-medium hover:file:bg-muted/70"
                />
              </div>

              {/* Botão baixar palco */}
              <div>
                <button
                  onClick={handleDownloadStage}
                  className="w-full h-10 rounded-lg bg-[blue] text-[white] border border-red-500/30 hover:bg-red-500/20 transition-colors"
                >
                  {__('Baixar Mapa de Referência')}
                </button>
              </div>

              {/* Botão deletar palco */}
              <div>
                <button
                  onClick={handleDeleteStage}
                  className="w-full h-10 rounded-lg bg-red-500/10 text-red-500 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                >
                  {__('Deletar Palco')}
                </button>
              </div>

              <Separator className="bg-border/50" />

              {/* Lista de luzes */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{__('Luzes')}</Label>

                {lights.map((light) => (
                  <div
                    key={light.id}
                    className="flex items-center gap-3 p-2 rounded-lg border border-border/50 bg-muted/20"
                  >
                    {/* Cor */}
                    <input
                      type="color"
                      value={light.color}
                      onChange={(e) =>
                        updateLight(light.id, { color: e.target.value })
                      }
                      className="w-10 h-10 p-1 bg-transparent"
                    />

                    {/* Intensidade */}
                    <input
                      type="range"
                      min="0"
                      max="5"
                      step="0.1"
                      value={light.intensity}
                      onChange={(e) =>
                        updateLight(light.id, {
                          intensity: parseFloat(e.target.value),
                        })
                      }
                      className="flex-1"
                    />

                    <span className="text-xs w-10 text-right">
                      {light.intensity.toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>

              <Separator className="bg-border/50" />

              {/* Stage Presets */}
              <div className="space-y-3 max-h-[calc(100vh-500px)] overflow-y-auto">
                <Label className="text-sm font-medium">{__('Palco Presets')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {STAGE_PRESETS.map(({ id, name }) => (
                    <button
                      key={id}
                      id={id}
                      onClick={() => applyStagePreset(id)}
                      className="stage-up flex items-center justify-center h-10 text-xs font-medium rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 hover:border-border transition-colors"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default BackgroundScene;